import { Typography, Grid, List } from "@mui/material";
import React from "react";
import { FormWrapper } from "../../../components/formWrapper";
import { InterestOptionItem } from "./interestOptionItem";
import { Tokens } from "configs";
import { WarningCard } from "../step5/warningCard";
import { LoadingModal } from "./loadingModal";
import { EnumCajuInterest } from "services/caju/enums/enumCajuInterest";
import { getDtoFromStep4FormValues } from "./helper";
import { QueuedUpdateContext } from "../queuedUpdateContext/context";
import { TagManagerService } from "services/tagManager";
import { EnumGTMEvents } from "services/tagManager/enums/enumGTMEvents";
import { LeadService } from "services/caju";
import { IStep3FormValues } from "../step3/useStep3Form/iStep3FormValues";
import { calculateProductMappings } from "../step3/productsList/productMapping";
import { Subscriptions } from "../step3/productsList/productConstants";
import { useFeatureFlag } from "services/featureFlag/useFeatureFlag";
import { FEATURE_FLAG_KEYS } from "services/featureFlag/featureFlagKeys";
import { Variables } from "configs/variables";

const RECORDED_DEMO_LP_URL =
  "https://produtos.caju.com.br/conhecendo-caju?utm_source=site&utm_medium=owned&utm_campaign=ctd_m_mr_site_xx_xx_m_cj_teste_v1_quero";

interface Step4Props {
  name?: string;
  step3Values?: IStep3FormValues;
  employeesNumber?: number | "";
  externalId: string;
  onGoNext: () => void;
  buildRegisterLink: () => string;
}

export const Step4 = (props: Step4Props) => {
  const [isOpenModal, setIsOpenModal] = React.useState(false);

  const hasLessThan10Employees =
    props?.employeesNumber && props?.employeesNumber < 10;

  const { subscriptions } = calculateProductMappings(
    props.step3Values?.selectedProducts || [],
  );
  const hasWellhubProduct = subscriptions.includes(
    Subscriptions.HEALTH_WELLHUB,
  );
  const dontAllowWellhubInCajuMais =
    hasLessThan10Employees && hasWellhubProduct;

  const isSignUpExpectedLeadPrefillEnabled = useFeatureFlag(
    FEATURE_FLAG_KEYS.SIGN_UP_EXPECTED_LEAD_PREFILL,
    false,
  );

  const queueContext = React.useContext(QueuedUpdateContext);

  const handleSignup = () => {
    TagManagerService.push(
      EnumGTMEvents.INTEREST_INFO_INTEREST_OPTION_SIGNUP_NOW__CLICK,
    );

    const data = getDtoFromStep4FormValues(
      props.externalId,
      EnumCajuInterest.ALREADY_KNOW,
      queueContext.id,
    );

    const expectedLeadUuid = queueContext.id;

    queueContext.push(data, LeadService.saveLead, () => {
      if (isSignUpExpectedLeadPrefillEnabled && expectedLeadUuid) {
        const baseUrl =
          process.env.REACT_APP_ENVIRONMENT === "PRODUCTION"
            ? Variables.signUpLink
            : Variables.stagingSignUpLink;
        window.open(`${baseUrl}?expected_lead_id=${expectedLeadUuid}`, "_self", "noopener noreferrer");
      } else {
        window.open(props.buildRegisterLink(), "_self", "noopener noreferrer");
      }
    });

    setIsOpenModal(true);
  };

  const handleWatchDemo = () => {
    TagManagerService.push(
      EnumGTMEvents.INTEREST_INFO_INTEREST_OPTION_SCHEDUALED_MEETING__CLICK,
    );

    const data = getDtoFromStep4FormValues(
      props.externalId,
      EnumCajuInterest.WATCH_DEMO,
      queueContext.id,
    );

    queueContext.push(data, LeadService.saveLead, () =>
      window.open(RECORDED_DEMO_LP_URL, "_blank", "noopener noreferrer"),
    );
  };

  const getFirstName = (name?: string) => name?.split(" ")[0];

  return (
    <FormWrapper>
      <>
        <LoadingModal open={isOpenModal} hasError={queueContext.error} />
        <Grid container>
          <Grid item xs={12} md={7} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ "&:first-letter": { textTransform: "capitalize" } }}
            >
              {getFirstName(props.name)}, escolha seu próximo passo:
            </Typography>
          </Grid>
          <Grid item xs={12} md={7}>
            <form data-cy="step4-form">
              <List
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                <InterestOptionItem
                  title={
                    <>
                      Criar cadastro e explorar a plataforma{" "}
                      <strong>(gratuito)</strong>
                    </>
                  }
                  description="O cadastro é rápido e gratuito e você já pode explorar a plataforma."
                  onClick={handleSignup}
                  disabled={queueContext.loading}
                />

                <InterestOptionItem
                  title="Ver material complementar"
                  description="Confira tudo que nosso time preparou para tirar suas dúvidas."
                  onClick={handleWatchDemo}
                  disabled={queueContext.loading}
                />
              </List>
            </form>
          </Grid>
          <Grid item xs={0} md={1}></Grid>
          {dontAllowWellhubInCajuMais && (
            <Grid item xs={12} md={4} mb={2}>
              <WarningCard
                title="Importante"
                isAccordion={false}
                open={true}
                setOpen={() => {}}
              >
                <>
                  <Typography
                    variant="body1"
                    color={Tokens.feedbackColor.warning.dark}
                  >
                    No momento a oferta de Wellhub está disponível para empresas
                    a partir de <strong>10 colaboradores</strong>.
                  </Typography>
                  <Typography
                    variant="body1"
                    color={Tokens.feedbackColor.warning.dark}
                  >
                    Não se preocupe, você pode seguir conhecendo Caju Mais com
                    os serviços da Conexa Saúde, Optum e SAT Ivoor.
                  </Typography>
                </>
              </WarningCard>
            </Grid>
          )}
        </Grid>
      </>
    </FormWrapper>
  );
};
